import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { Role, RoleMember, RoleMemberAvailable, UserRoleEntry } from "@/types";

// #274: Rollen-Achse (Fach-Rollen + kuratierte System-Rollen). Verwaltung ist admin-only.

export function useRoles(): UseQueryResult<Role[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: queryKeys.list("roles"),
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<Role[]>("/roles");
    },
    enabled: !!session,
  });
}

export function useRole(id: string | undefined): UseQueryResult<Role> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("roles", id) : ["resources", "roles", "_invalid"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<Role>(`/roles/${id}`);
    },
    enabled: !!session && !!id,
  });
}

export function useRoleMembers(id: string | undefined): UseQueryResult<RoleMember[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.subResource("roles", id, "members") : ["resources", "roles", "_invalid", "members"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<RoleMember[]>(`/roles/${id}/members`);
    },
    enabled: !!session && !!id,
  });
}

/**
 * #330 (UC-1): die Mitglieder einer Fach-Rolle für den Personen-Picker an der Einrichtung
 * (`GET /roles/:id/members/available` aus #329 UC-4).
 *
 * Bewusst ein EIGENER Read neben `useRoleMembers`: er liefert nur `{userId, anzeigeName}`
 * (PII-minimal, ohne Zuweisungs-Herkunft und ohne Scope) und ist deshalb auch für den
 * Inhaber einer Einrichtung lesbar, der die Rollen-Verwaltung nicht sehen darf.
 *
 * ⚠ SEIT #331 nur noch der **Skew-Rückfall** der Entscheider-Karte: die reguläre Quelle ist
 * das Firmen-Adressbuch (`useAvailableUsers`), weil das Setup beliebige interne Personen
 * benennen darf. Diese Route liefert die (globalen) Mitglieder EINER Rolle — eine echte
 * Teilmenge dessen, was gewählt werden darf. Wo sie einspringt, muss das dranstehen.
 *
 * ⚠ `instanceId` ist PFLICHT — am Wire wie hier im Typ (ohne den Parameter antwortet der
 * Master generisch mit 400); er ist das Sicht-Gate (Inhaber ∪ Admin der Einrichtung). Eine
 * Verdrängungs-Logik gibt es dahinter NICHT mehr: der Zuweisungs-Geltungsbereich ist mit
 * #331 ersatzlos gestrichen, alle Zuweisungen sind global.
 *
 * `enabled` bleibt steuerbar: ohne freigegebene Personen-Wahl wird gar nicht erst abgerufen.
 */
export function useRoleMembersAvailable(
  roleId: string | undefined,
  options: { instanceId: string; enabled?: boolean },
): UseQueryResult<RoleMemberAvailable[]> {
  const { session, logout } = useAuth();
  const instanceId = options.instanceId;
  return useQuery({
    // Die Einrichtung gehört in den Schlüssel: dieselbe Rolle hat je Einrichtung eine andere
    // wirksame Besetzung, und ein geteilter Cache zeigte die der zuletzt geöffneten.
    queryKey: roleId
      ? [...queryKeys.subResource("roles", roleId, "members-available"), instanceId]
      : ["resources", "roles", "_invalid", "members-available"],
    queryFn: async () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      const r = await client.get<{ members: RoleMemberAvailable[] }>(
        `/roles/${roleId}/members/available?instanceId=${encodeURIComponent(instanceId)}`,
      );
      // Skew-fest: eine Antwort ohne `members` ist keine leere Rolle — sie ist unbrauchbar,
      // und ein `[]` daraus zu machen behauptete „diese Rolle hat niemanden".
      if (!Array.isArray(r?.members)) throw new Error("members fehlen");
      return r.members;
    },
    enabled: !!session && !!roleId && options.enabled !== false,
  });
}

// Rollen EINES Users (User-Detail-Karte). Endpoint erlaubt Admin ODER den User selbst.
export function useUserRoles(userId: string | undefined): UseQueryResult<UserRoleEntry[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: userId ? queryKeys.subResource("users", userId, "roles") : ["resources", "users", "_invalid", "roles"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<UserRoleEntry[]>(`/users/${userId}/roles`);
    },
    enabled: !!session && !!userId,
  });
}
